import { useState, useEffect, useMemo } from 'react'
import { usePlayerStore } from '../../stores'
import { apiClient } from '../../utils/api'
import { backendClient } from '../../utils/backend'
import {
  FileText, Users, Shield,
  Download, TrendingUp
} from 'lucide-react'

export default function AdminReports() {
  const { players } = usePlayerStore()
  const [profiles, setProfiles] = useState<any[]>([])
  const [matchReports, setMatchReports] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [filterType, setFilterType] = useState<'all' | 'match' | 'scout' | 'user'>('all')
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({ from: '', to: '' })

  useEffect(() => {
    async function load() {
      try {
        const [p, m] = await Promise.all([
          apiClient.getProfiles().catch(() => []),
          backendClient.from('match_reports').select('*').order('created_at', { ascending: false }).limit(200).then(r => r.data || []),
        ])
        setProfiles(Array.isArray(p) ? p : [])
        setMatchReports(Array.isArray(m) ? m : [])
      } catch { } finally { setLoading(false) }
    }
    load()
  }, [])

  const stats = useMemo(() => {
    const totalMatchReports = matchReports.length
    const totalUsers = profiles.length
    const totalPlayers = players.length
    const reportsThisMonth = matchReports.filter((r: any) => {
      const d = new Date(r.created_at || 0)
      const now = new Date()
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
    }).length
    return { totalMatchReports, totalUsers, totalPlayers, reportsThisMonth }
  }, [matchReports, profiles, players])

  const filteredReports = useMemo(() => {
    return matchReports.filter((r: any) => {
      if (filterType !== 'all' && r.report_type !== filterType) return false
      if (dateRange.from && new Date(r.created_at) < new Date(dateRange.from)) return false
      if (dateRange.to && new Date(r.created_at) > new Date(dateRange.to)) return false
      return true
    })
  }, [matchReports, filterType, dateRange])

  const handleExport = () => {
    const data = filteredReports.map((r: any) => ({
      ID: r.id,
      Type: r.report_type,
      Title: r.title,
      Player: r.player_name,
      Club: r.club_name,
      Author: r.author_name,
      Status: r.status,
      Created: new Date(r.created_at).toLocaleDateString(),
    }))
    const csv = [Object.keys(data[0] || {}), ...data.map(Object.values)]
      .map(row => row.map(v => `"${v}"`).join(','))
      .join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `admin-reports-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin text-green-500 text-4xl mb-4">📊</div>
          <p className="text-gray-400">Loading reports...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-white">Reports</h1>
          <p className="mt-1 text-gray-400">View and export platform reports</p>
        </div>
        <button
          onClick={handleExport}
          disabled={filteredReports.length === 0}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#7cff01]/10 border border-[#7cff01]/20 text-[#7cff01] hover:bg-[#7cff01]/20 transition text-sm font-bold uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download size={18} />
          Export CSV
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="stagger-card animate-fade-in-up bg-[#161b22] border border-gray-800 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="text-gray-400 text-[10px] font-black uppercase tracking-widest">Match Reports</h3>
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center"><FileText size={18} className="text-blue-400" /></div>
          </div>
          <p className="text-3xl font-black text-white mt-2">{stats.totalMatchReports}</p>
        </div>
        <div className="stagger-card animate-fade-in-up bg-[#161b22] border border-gray-800 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="text-gray-400 text-[10px] font-black uppercase tracking-widest">This Month</h3>
            <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center"><TrendingUp size={18} className="text-green-400" /></div>
          </div>
          <p className="text-3xl font-black text-white mt-2">{stats.reportsThisMonth}</p>
        </div>
        <div className="stagger-card animate-fade-in-up bg-[#161b22] border border-gray-800 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="text-gray-400 text-[10px] font-black uppercase tracking-widest">Total Users</h3>
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center"><Users size={18} className="text-purple-400" /></div>
          </div>
          <p className="text-3xl font-black text-white mt-2">{stats.totalUsers}</p>
        </div>
        <div className="stagger-card animate-fade-in-up bg-[#161b22] border border-gray-800 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="text-gray-400 text-[10px] font-black uppercase tracking-widest">Total Players</h3>
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center"><Shield size={18} className="text-orange-400" /></div>
          </div>
          <p className="text-3xl font-black text-white mt-2">{stats.totalPlayers}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-5">
        <div className="flex flex-wrap gap-4">
          <div className="flex gap-2">
            {['all', 'match', 'scout', 'user'].map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type as any)}
                className={`px-4 py-2 rounded-xl text-sm font-bold transition ${
                  filterType === type
                    ? 'bg-[#7cff01]/10 border-[#7cff01]/20 text-[#7cff01]'
                    : 'bg-white/5 border-white/5 text-gray-400 hover:text-white hover:border-white/10'
                }`}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="date"
              value={dateRange.from}
              onChange={e => setDateRange(d => ({ ...d, from: e.target.value }))}
              className="px-4 py-2 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm"
            />
            <input
              type="date"
              value={dateRange.to}
              onChange={e => setDateRange(d => ({ ...d, to: e.target.value }))}
              className="px-4 py-2 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm"
            />
          </div>
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-[#161b22] border border-gray-800 rounded-2xl overflow-hidden">
        {filteredReports.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="mx-auto text-gray-500 mb-4" size={48} />
            <h3 className="text-lg font-semibold text-white mb-2">No reports found</h3>
            <p className="text-gray-400">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5 bg-gray-950">
                  <th className="p-4 text-left text-[10px] font-black uppercase tracking-widest text-gray-500">Type</th>
                  <th className="p-4 text-left text-[10px] font-black uppercase tracking-widest text-gray-500">Title</th>
                  <th className="p-4 text-left text-[10px] font-black uppercase tracking-widest text-gray-500">Player / Club</th>
                  <th className="p-4 text-left text-[10px] font-black uppercase tracking-widest text-gray-500">Author</th>
                  <th className="p-4 text-left text-[10px] font-black uppercase tracking-widest text-gray-500">Status</th>
                  <th className="p-4 text-left text-[10px] font-black uppercase tracking-widest text-gray-500">Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.map((report: any) => (
                  <tr key={report.id} className="border-b border-white/5 hover:bg-white/5 transition">
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                        report.report_type === 'match' ? 'bg-blue-500/10 text-blue-400' :
                        report.report_type === 'scout' ? 'bg-purple-500/10 text-purple-400' :
                        'bg-orange-500/10 text-orange-400'
                      }`}>
                        {report.report_type}
                      </span>
                    </td>
                    <td className="p-4">
                      <p className="text-sm font-semibold text-white truncate max-w-xs">{report.title || 'Untitled'}</p>
                    </td>
                    <td className="p-4">
                      <p className="text-sm text-white truncate max-w-xs">{report.player_name || report.club_name || 'N/A'}</p>
                    </td>
                    <td className="p-4">
                      <p className="text-sm text-gray-400">{report.author_name || 'Unknown'}</p>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                        report.status === 'published' ? 'bg-green-500/10 text-green-400' :
                        report.status === 'draft' ? 'bg-yellow-500/10 text-yellow-400' :
                        'bg-red-500/10 text-red-400'
                      }`}>
                        {report.status}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-gray-400">
                      {new Date(report.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}